import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import AnttButton from "../../../vendors/components/Button";
import If from "../../../vendors/components/If";
import DataTempoPipe from "../../../vendors/pipe/DataTempoPipe";
import AuthService from "../../../vendors/services/authService";
import EventoService from "../../services/evento.service";
import InformeCard from "../../components/informe-card";
import SafeHtml from "../../components/safe-html";
import {
  InformeEvento,
  isInformeEvento,
  isInformeSistema,
} from "../../shared/@types/informeEvento";
import { useInformesEvento } from "../../shared/contexts/InformesEventoContext";
import "./informes-evento.scss";

interface EventoResumo {
  numero?: string | number;
  ano?: string | number;
  tipo?: string;
  situacao?: string;
  carregando: boolean;
}

interface GrupoEvento {
  nuEventoVinculo: number;
  informes: InformeEvento[];
}

export default function InformesEvento() {
  const history = useHistory();
  const authService = new AuthService();
  const isAuthenticated = authService.isAuthenticated();
  const { informes, loading, error } = useInformesEvento();

  const [informeSelecionado, setInformeSelecionado] =
    useState<InformeEvento | null>(null);
  const [eventosResumo, setEventosResumo] = useState<
    Record<number, EventoResumo>
  >({});
  const eventoService = useRef(new EventoService());

  const informesSistema = useMemo(
    () => informes.filter((i) => isInformeSistema(i)),
    [informes]
  );

  const gruposEvento = useMemo<GrupoEvento[]>(() => {
    const mapa = new Map<number, InformeEvento[]>();
    informes
      .filter((i) => isInformeEvento(i))
      .forEach((informe) => {
        const chave = informe.nuEventoVinculo;
        if (!mapa.has(chave)) {
          mapa.set(chave, []);
        }
        mapa.get(chave)!.push(informe);
      });
    return Array.from(mapa.entries()).map(([nuEventoVinculo, lista]) => ({
      nuEventoVinculo,
      informes: lista,
    }));
  }, [informes]);

  // Uma chamada getEvento por evento distinto (N+1). Aceitável com poucos
  // comunicados vigentes; se o volume crescer, avaliar endpoint em lote.
  useEffect(() => {
    gruposEvento.forEach((grupo) => {
      const id = grupo.nuEventoVinculo;
      if (!id || eventosResumo[id]) {
        return;
      }
      setEventosResumo((prev) => ({
        ...prev,
        [id]: { carregando: true },
      }));
      eventoService.current
        .getEvento(id, 0)
        .then((res: any) => {
          const evento = res?.result;
          if (evento) {
            setEventosResumo((prev) => ({
              ...prev,
              [id]: {
                numero: evento.nuAudienciaPublica,
                ano: evento.nuAno,
                tipo: evento.cdTpEventoNavigation?.dsTpEvento,
                situacao:
                  evento.codSituacaoAudienciaNavigation?.dsSituacaoAudiencia,
                carregando: false,
              },
            }));
          } else {
            setEventosResumo((prev) => ({
              ...prev,
              [id]: { carregando: false },
            }));
          }
        })
        .catch((err) => {
          console.error("Erro ao carregar resumo do evento", id, err);
          setEventosResumo((prev) => ({
            ...prev,
            [id]: { carregando: false },
          }));
        });
    });
  }, [gruposEvento]);

  const acessarEvento = (nuEventoVinculo: number) => {
    if (isAuthenticated) {
      history.push("/evento/visualizar", { id: nuEventoVinculo });
    } else {
      history.push("/public/evento/visualizar", { id: nuEventoVinculo });
    }
  };

  const tituloGrupo = (grupo: GrupoEvento) => {
    const resumo = eventosResumo[grupo.nuEventoVinculo];
    if (resumo && (resumo.numero || resumo.tipo)) {
      const tipo = resumo.tipo || "Evento";
      const numero = resumo.numero ?? grupo.nuEventoVinculo;
      const ano = resumo.ano ? `/${resumo.ano}` : "";
      return `${tipo} nº ${numero}${ano}`;
    }
    return `Evento nº ${grupo.nuEventoVinculo}`;
  };

  return (
    <div className="informes-evento">
      <If condition={loading}>
        <div className="informes-evento-estado">
          <ProgressSpinner
            style={{ width: "40px", height: "40px" }}
            strokeWidth="4"
          />
          <span>Carregando comunicados...</span>
        </div>
      </If>

      <If condition={!loading && error}>
        <div className="informes-evento-estado">
          Não foi possível carregar os comunicados no momento.
        </div>
      </If>

      <If
        condition={
          !loading &&
          !error &&
          informesSistema.length === 0 &&
          gruposEvento.length === 0
        }
      >
        <div className="informes-evento-estado">
          Nenhum comunicado ou alerta vigente.
        </div>
      </If>

      <If condition={!loading && !error && informesSistema.length > 0}>
        <section className="informe-bloco informe-bloco-sistema">
          <header className="informe-bloco-header informe-bloco-header-sistema">
            <i className="pi pi-exclamation-triangle" />
            <span>Sistema</span>
          </header>
          <div className="informe-bloco-cards">
            {informesSistema.map((informe) => (
              <InformeCard
                key={informe.cdSeqInformeEvento}
                informe={informe}
                modo="resumo"
                onClick={() => setInformeSelecionado(informe)}
              />
            ))}
          </div>
        </section>
      </If>

      {gruposEvento.map((grupo) => (
        <section
          key={grupo.nuEventoVinculo}
          className="informe-bloco informe-bloco-evento"
        >
          <header className="informe-bloco-header informe-bloco-header-evento">
            <div className="informe-bloco-header-info">
              <i className="pi pi-volume-up" />
              <span className="informe-bloco-titulo">{tituloGrupo(grupo)}</span>
              {/* Situacao do evento ocultada temporariamente
              <If condition={!!eventosResumo[grupo.nuEventoVinculo]?.situacao}>
                <span className="informe-bloco-situacao">
                  {eventosResumo[grupo.nuEventoVinculo]?.situacao}
                </span>
              </If>
              */}
            </div>
            <AnttButton
              label="Acessar evento"
              className="p-button-sm"
              onClick={() => acessarEvento(grupo.nuEventoVinculo)}
            />
          </header>
          <div className="informe-bloco-cards">
            {grupo.informes.map((informe) => (
              <InformeCard
                key={informe.cdSeqInformeEvento}
                informe={informe}
                modo="resumo"
                onClick={() => setInformeSelecionado(informe)}
              />
            ))}
          </div>
        </section>
      ))}

      <Dialog
        header={informeSelecionado?.noInforme}
        visible={!!informeSelecionado}
        draggable={false}
        className="informe-dialog"
        style={{ width: "600px", maxWidth: "95vw" }}
        onHide={() => setInformeSelecionado(null)}
      >
        <If condition={!!informeSelecionado}>
          <div className="informe-dialog-vigencia">
            Vigência: <DataTempoPipe value={informeSelecionado?.dtInicioInforme} />{" "}
            até <DataTempoPipe value={informeSelecionado?.dtFimInforme} />
          </div>
          <SafeHtml
            className="informe-dialog-conteudo"
            html={informeSelecionado?.dsInforme || ""}
          />
        </If>
      </Dialog>
    </div>
  );
}
